import React, { useState } from "react";
import { toast } from "sonner";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill } from "../components/tag/Primitives";
import { api } from "../lib/api";

const deliverables = [
  { title: "AI-Generated Build-Ready Report", body: "A 0-100 Build-Ready Score plus a 7-dimension breakdown across website, email, opt-in, where-you-show-up, packaged offers, coherent system, and next-move readiness. Tailored to what you already know and where you are — not a generic template." },
  { title: "Top 3 Opportunities to Package", body: "The three highest-leverage things you could package next, in priority order. Specific. Actionable. No fluff." },
  { title: "Top 3 Build-Moves This Month", body: "The three most productive moves you could make in the next 30 days. Each with effort, impact, and a starting step." },
  { title: "Bridge to the Blueprint", body: "A clear next step toward the $497 Blueprint — only if a bigger build actually fits your situation. Otherwise we tell you straight." },
];

export default function Audit() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const buy = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/audit/checkout", {
        email,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not create checkout session.");
      }
    } catch (e) {
      toast.error("Checkout error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Package what you already know | The Authority Gap" path="/audit">
      <section style={{ background: "#090909", padding: "70px 32px 48px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 880, margin: "0 auto" }}>
          <div style={{ marginBottom: 24 }}><EyebrowPill>Build-Ready Audit</EyebrowPill></div>
          <h1 className="tag-h1" style={{ marginBottom: 18 }}>See exactly how to package what you already know.</h1>
          <p style={{ color: "#aaa", fontSize: 15, lineHeight: 1.6 }}>
            A personalized, AI-generated read on what you could build from where you are today — scored, sharp, and in your inbox in minutes. No filler. No &ldquo;5 reasons to post more.&rdquo; Just the opportunity, the moves that would compound, and where to start this week.
          </p>
        </div>
      </section>

      <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "44px 32px" }}>
        <div className="tag-audit-grid" style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 360px", gap: 24, alignItems: "start" }}>
          <div>
            {deliverables.map((d) => (
              <div key={d.title} className="tag-card" style={{ marginBottom: 10 }} data-testid={`deliverable-${d.title.toLowerCase().replace(/\s/g, "-")}`}>
                <h3 style={{ color: "#fff", fontSize: 14, fontWeight: 700, margin: "0 0 8px" }}>{d.title}</h3>
                <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.65, margin: 0 }}>{d.body}</p>
              </div>
            ))}
          </div>

          <div className="tag-card" style={{ position: "sticky", top: 80 }}>
            <div style={{ color: "#aaa", fontSize: 13, fontWeight: 600, marginBottom: 16 }}>Authority Audit</div>
            <div style={{ color: "#fff", fontSize: 48, fontWeight: 700, marginBottom: 4, lineHeight: 1 }}>$49</div>
            <div style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>One-time. No subscription.</div>
            <div style={{ borderTop: "1px solid #1e1e1e", paddingTop: 16, marginBottom: 16 }}>
              {[
                "Delivered in minutes, not days",
                "Personalized to your site + niche",
                "Branded PDF you can share with your team",
                "Bridge to the $497 Blueprint if it fits",
              ].map((t) => (
                <div key={t} style={{ display: "flex", gap: 9, alignItems: "baseline", marginBottom: 6, fontSize: 13, color: "#bbb", fontWeight: 500 }}>
                  <span style={{ color: "#FF6575", fontWeight: 700 }}>✓</span>{t}
                </div>
              ))}
            </div>
            <input
              data-testid="audit-email-input"
              type="email"
              placeholder="your@email.com"
              className="tag-input"
              style={{ marginBottom: 10 }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button data-testid="audit-checkout-btn" type="button" className="btn-primary" onClick={buy} disabled={submitting} style={{ width: "100%" }}>
              {submitting ? "Redirecting…" : "Get Your Authority Audit — $49"}
            </button>
            <p style={{ color: "#777", fontSize: 11, marginTop: 10, margin: "10px 0 0" }}>
              After payment you&apos;ll receive a short intake form to complete.
            </p>
          </div>
        </div>
        <style>{`
          @media (max-width: 880px) { .tag-audit-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>
    </Layout>
  );
}
